// Privacy / personvern copy — bilingual, mirrors Nettpilot's policy at
// https://nettpilot.no/personvern/ and adds AI-twin-specific disclosures.

export const PRIVACY_COPY = {
  no: {
    nav_back: "← Tilbake",
    title: "Personvern",
    updated: "Sist oppdatert: februar 2026",
    intro:
      "Dette er personvernerklæringen for den digitale tvillingen av Nickolass på denne siden. Den følger samme strenge konfidensialitets- og GDPR-retningslinjer som Nettpilot.no, og utfyller den med detaljer som er spesifikke for AI-samtaler.",
    controller_title: "Behandlingsansvarlig",
    controller_body:
      "Nettpilot AS, Tosobakken 11, 3520 Jevnaker, Norge.\nTelefon: +47 400 43 101 — e-post via skjemaet på forsiden eller nettpilot.no/kontakt.",
    sections: [
      {
        h: "Hva vi samler inn",
        p: [
          "Når du chatter med den digitale tvillingen lagrer vi: melding, svar fra modellen, valgt språk (NO/EN), en tilfeldig generert økt-ID (UUID), og tidspunkt.",
          "IP-adresse brukes kun i minnet for å hindre misbruk (rate limiting) og lagres ikke i databasen.",
          "Hvis du fyller ut kontaktskjemaet sender vi navn, e-post og melding videre til Nickolass via Postmark. Har du chattet med tvillingen kan du velge å legge ved samtalen (avkrysningsboks, på som standard).",
          "Taleinput: når du bruker mikrofonen sendes lydopptaket til OpenAI Whisper for transkribering. Lyden lagres ikke hos oss — kun den transkriberte teksten blir en del av samtalen.",
          "Henvendelser via skjemaet oppsummeres automatisk i én setning av en AI-modell (Claude) slik at Nickolass raskt ser behovet. Oppsummeringen lagres sammen med henvendelsen.",
        ],
      },
      {
        h: "Hvorfor vi behandler dataene",
        p: [
          "Berettiget interesse (GDPR art. 6(1)(f)): drift og forbedring av en gratis selvbetjeningstjeneste, og forhindring av misbruk.",
          "Samtykke ved kontaktskjema (GDPR art. 6(1)(a)): du gir oss kontaktinfo for å få svar.",
        ],
      },
      {
        h: "Hvor lenge vi lagrer",
        p: [
          "Chat-meldinger slettes automatisk etter 90 dager via Mongo TTL-indeks.",
          "Henvendelser via kontaktskjemaet lagres så lenge det er nødvendig for å besvare dem og oppfylle eventuelle avtaler — typisk inntil 3 år.",
          "Du kan be om sletting eller eksport tidligere — se «Dine rettigheter» nedenfor.",
        ],
      },
      {
        h: "AI-tjenester og databehandlere",
        p: [
          "Selve modellen er Claude Sonnet 5.5 fra Anthropic, levert via Emergent som proxy. Stemmesyntese (når aktivert) leveres av ElevenLabs. E-postutsendelse leveres av Postmark.",
          "Dette innebærer at meldingstekst sendes til USA. Leverandørene følger EU-EØS standardkontraktklausuler (SCCs).",
          "Bare nøkkelpersonell hos Nettpilot og våre leverandører har tilgang til samtalene, og innholdet brukes ikke til å trene generelle modeller.",
        ],
      },
      {
        h: "Nyhetsbrev",
        p: [
          "Melder du deg på nyhetsbrevet, lagrer vi e-postadressen, valgt språk og tidspunkt for påmelding og bekreftelse. Grunnlaget er samtykket ditt, som du gir ved å bekrefte via lenken vi sender (dobbel bekreftelse).",
          "Du får én e-post hver fredag med ukens nye blogginnlegg. Hver e-post har en avmeldingslenke som virker med ett klikk. Ved avmelding sender vi ikke mer, og du kan be oss slette adressen helt.",
          "Utsendelse skjer via Postmark.",
        ],
      },
      {
        h: "Dine rettigheter (GDPR)",
        p: [
          "Du har rett til innsyn, retting, sletting, dataportabilitet og innsigelse.",
          "Innsyn/eksport: hent samtalen din i JSON via «Eksporter» i chatten, eller GET /api/chat/export/{session_id}.",
          "Sletting: bruk «Slett samtale» i chatten, eller DELETE /api/chat/session/{session_id}.",
          "For henvendelser om data fra kontaktskjemaet eller klage til Datatilsynet — kontakt oss på +47 400 43 101 eller via nettpilot.no/kontakt.",
        ],
      },
      {
        h: "Cookies",
        p: [
          "Vi setter ingen tracking cookies. Vi lagrer din språkvalg (NO/EN) lokalt i nettleseren (localStorage). Det er det.",
        ],
      },
      {
        h: "Sikkerhet",
        p: [
          "All trafikk går over HTTPS. Databasen kjører i et privat kubernetes-cluster. API-nøkler ligger i miljøvariabler, aldri i kildekoden.",
        ],
      },
    ],
    sign_off: "Mvh,\nNickolass Jensen — Nettpilot AS",
  },
  en: {
    nav_back: "← Back",
    title: "Privacy",
    updated: "Last updated: February 2026",
    intro:
      "This is the privacy policy for the digital twin of Nickolass on this site. It follows the same strict confidentiality and GDPR guidelines as Nettpilot.no, with extra detail specific to AI conversations.",
    controller_title: "Data controller",
    controller_body:
      "Nettpilot AS, Tosobakken 11, 3520 Jevnaker, Norway.\nPhone: +47 400 43 101 — email via the contact form on the homepage or nettpilot.no/kontakt.",
    sections: [
      {
        h: "What we collect",
        p: [
          "When you chat with the digital twin we store: your message, the model's reply, the chosen language (NO/EN), a randomly-generated session ID (UUID), and a timestamp.",
          "Your IP address is held only in memory for rate-limiting and is never persisted to the database.",
          "If you submit the contact form, we forward name, email and message to Nickolass via Postmark. If you chatted with the twin, you can choose to attach the conversation (checkbox, on by default).",
          "Voice input: when you use the microphone, the recording is sent to OpenAI Whisper for transcription. We do not store the audio — only the transcribed text becomes part of the chat.",
          "Contact-form submissions are automatically summarised in one sentence by an AI model (Claude) so Nickolass can see the need at a glance. The summary is stored with the submission.",
        ],
      },
      {
        h: "Why we process this",
        p: [
          "Legitimate interest (GDPR art. 6(1)(f)): operating and improving a free self-service tool and preventing abuse.",
          "Consent (GDPR art. 6(1)(a)) when you submit the contact form so we can reply.",
        ],
      },
      {
        h: "How long we keep it",
        p: [
          "Chat messages are auto-deleted after 90 days via a MongoDB TTL index.",
          "Contact-form submissions are kept as long as needed to answer and honour any agreement — typically up to 3 years.",
          "You can request export or deletion sooner — see «Your rights» below.",
        ],
      },
      {
        h: "AI services and processors",
        p: [
          "The model itself is Anthropic's Claude Sonnet 5.5, served via Emergent as a proxy. Voice synthesis (when enabled) is provided by ElevenLabs. Email delivery is by Postmark.",
          "This means message text is transmitted to the US. The processors operate under EU-EEA Standard Contractual Clauses (SCCs).",
          "Only key Nettpilot personnel and our processors can access the conversations, and the content is not used to train general-purpose models.",
        ],
      },
      {
        h: "Newsletter",
        p: [
          "If you subscribe to the newsletter, we store your email address, chosen language and the time of signup and confirmation. The legal basis is your consent, given by confirming via the link we send (double opt-in).",
          "You get one email every Friday with the week's new blog posts. Every email has a one-click unsubscribe link. After unsubscribing we send nothing more, and you can ask us to delete the address completely.",
          "Delivery is handled by Postmark.",
        ],
      },
      {
        h: "Your rights (GDPR)",
        p: [
          "You have the right of access, rectification, erasure, portability and objection.",
          "Access / export: use the «Export» button in the chat, or GET /api/chat/export/{session_id}.",
          "Erasure: use the «Delete chat» button in the chat, or DELETE /api/chat/session/{session_id}.",
          "For data from the contact form, or to lodge a complaint with the Norwegian DPA (Datatilsynet) — contact us at +47 400 43 101 or via nettpilot.no/kontakt.",
        ],
      },
      {
        h: "Cookies",
        p: [
          "No tracking cookies. We store your language choice (NO/EN) locally in your browser (localStorage). That's it.",
        ],
      },
      {
        h: "Security",
        p: [
          "All traffic is over HTTPS. The database runs inside a private Kubernetes cluster. API keys live in environment variables, never in source code.",
        ],
      },
    ],
    sign_off: "Cheers,\nNickolass Jensen — Nettpilot AS",
  },
};
